import type { TaskStatus } from "./api.js";

export type TaskFilter = "all" | "waiting" | "active" | "done" | "attention" | "closed";

const activeStatuses = new Set<TaskStatus>([
  "DRAFT",
  "UPLOADING",
  "PROCESSING",
  "NEEDS_INPUT",
  "EXECUTING",
  "UNKNOWN",
]);

const failures: Record<string, { summary: string; nextStep: string }> = {
  AUDIO_PROBE_FAILED: {
    summary: "语音文件无法识别",
    nextStep: "请重新录音或选择其他录音。",
  },
  MODEL_UNAVAILABLE: {
    summary: "AI模型暂时不可用",
    nextStep: "资料已保留，请稍后重试。",
  },
  MODEL_REJECTED: {
    summary: "AI没有接受本次资料",
    nextStep: "请检查资料内容后重新提交。",
  },
  PROCESSING_FAILED: {
    summary: "资料整理没有完成",
    nextStep: "资料已保留，请重新提交；仍失败时联系管理员。",
  },
  PRODUCT_NOT_FOUND: {
    summary: "没有找到对应产品",
    nextStep: "请核对款号，或先在样品系统建立产品后再试。",
  },
  POSSIBLE_DUPLICATE: {
    summary: "近期已经登记过这个样品",
    nextStep: "为避免重复，系统没有再次写入。请确认是否确实是另一批新样。",
  },
  DRAFT_VALIDATION_FAILED: {
    summary: "整理结果需要重新确认",
    nextStep: "请重新提交或修改资料后再试。",
  },
  SENSITIVE_CONTENT_BLOCKED: {
    summary: "资料不适合外发处理",
    nextStep: "请删除敏感内容后重新提交。",
  },
  SAMPLE_API_FAILED: {
    summary: "样品系统写入失败",
    nextStep: "请查看详情，避免重复创建。",
  },
};

export function taskMatchesFilter(status: TaskStatus, filter: TaskFilter): boolean {
  if (filter === "all") return true;
  if (filter === "waiting") return status === "AWAITING_CONFIRMATION";
  if (filter === "active") return activeStatuses.has(status);
  if (filter === "done") return status === "SUCCEEDED";
  if (filter === "attention") return status === "FAILED" || status === "EXPIRED";
  return status === "CANCELED";
}

export function taskFailurePresentation(
  code: string | null | undefined,
): { summary: string; nextStep: string } {
  return (
    (code ? failures[code] : undefined) ?? {
      summary: "任务未完成",
      nextStep: "请查看详情或联系管理员。",
    }
  );
}

export function formatBusinessDateTime(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "时间未知";
  const parts = new Intl.DateTimeFormat("zh-CN", {
    timeZone: "Asia/Shanghai",
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(date);
  const byType = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return (
    byType.year +
    "/" +
    Number(byType.month) +
    "/" +
    Number(byType.day) +
    " " +
    byType.hour +
    ":" +
    byType.minute
  );
}
